/**
 * Block-off arithmetic (DNKADM-0373) — the translation layer between what an operator
 * says ("block Bay 2 from 9 to 12") and what dink-api records (a date exception that
 * says WHEN THE BAY IS OPEN that day). The model is expressed the wrong way round for
 * a human, so the console does the inversion here, once, as pure functions, and both
 * targets (a bay's resource exception, a coach's staff exception) go through the same
 * code — never two nearly-identical implementations.
 *
 * Every time is a wall-clock `"HH:mm"` in the location's timezone, exactly as the
 * schedule and exception surfaces already carry it (`@/utils/shiftValidation`'s
 * `ShiftLike`, end-of-day spelled `"00:00"`). No instants, no naive-time round trip.
 *
 * The rules this module encodes (spec "What it writes" / "Computing the bay's effective
 * shifts", as CORRECTED 2026-09-05 against dink-api's `ScheduleResolver`):
 *  - **the base for a date is the target's weekly row for that weekday, and only that.**
 *    No weekly row, a row marked unavailable, or a row available with zero shifts all mean
 *    CLOSED — the target is already unbookable that day and NOTHING is written for it
 *    (writing an exception there would create availability where there was none). There
 *    is no inheritance of the location window at the weekly rung; `USE_LOCATION_WINDOW`
 *    exists only on the date-exception rung (an available exception with no shifts);
 *  - **a window block** — the base MINUS the blocked range, written as an available
 *    exception; if nothing is left, the all-day CLOSED form instead (never an available
 *    exception with zero shifts, which would read as "use the location window");
 *  - **all day** — `isAvailable: false`, no shifts (never a 00:00–23:59 range).
 */
import type { Reservation } from '@/api/reservations';
import { localHourOf } from './calendar/derive';
import {
  END_OF_DAY_MINUTES,
  shiftEndMinutes,
  shiftStartMinutes,
  type ShiftLike,
} from '@/utils/shiftValidation';

/** The two things a block can target — never one mixed list. */
export type BlockTargetKind = 'bay' | 'coach';

/** A half-open minute-of-day range `[start, end)`; `end` may be 1440 (end of day). */
export interface MinuteRange {
  start: number;
  end: number;
}

/** The exception body the write needs — the two forms the spec allows, nothing else. */
export interface BlockWrite {
  isAvailable: boolean;
  shifts: ShiftLike[];
}

/** A schedule row as both the resource and staff schedules shape it. */
export interface ScheduleRowLike {
  isAvailable: boolean;
  shifts: ShiftLike[];
}

/** `"HH:mm"` for a minute-of-day; 1440 (end of day) is spelled `"00:00"` on the wire. */
export function minutesToClock(minutes: number): string {
  const m = minutes >= END_OF_DAY_MINUTES ? 0 : minutes;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/** Parse shifts to sorted, well-formed minute ranges; a malformed shift is dropped. */
export function toMinuteRanges(shifts: ShiftLike[]): MinuteRange[] {
  const ranges: MinuteRange[] = [];
  for (const shift of shifts) {
    const start = shiftStartMinutes(shift.startTime);
    const end = shiftEndMinutes(shift.endTime);
    if (start === null || end === null || start >= end) continue;
    ranges.push({ start, end });
  }
  return ranges.sort((a, b) => a.start - b.start || a.end - b.end);
}

/** Minute ranges back to wire shifts. */
export function toShifts(ranges: MinuteRange[]): ShiftLike[] {
  return ranges.map((r) => ({
    startTime: minutesToClock(r.start),
    endTime: minutesToClock(r.end),
  }));
}

/**
 * The 0=Sunday … 6=Saturday weekday of a `YYYY-MM-DD` calendar date, read as a plain
 * calendar day (no timezone involved — the same digits give the same weekday
 * everywhere). `null` for an unparseable value.
 */
export function dayOfWeekOf(dateValue: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return Number.isNaN(d.getTime()) ? null : d.getUTCDay();
}

/** The weekday names, `dayOfWeek` 0=Sunday … 6=Saturday, for "already closed on Mondays". */
export const WEEKDAY_PLURALS = [
  'Sundays',
  'Mondays',
  'Tuesdays',
  'Wednesdays',
  'Thursdays',
  'Fridays',
  'Saturdays',
] as const;

/**
 * A target's base shifts for a date (the load-bearing computation, corrected 2026-09-05):
 * its weekly row for that weekday when the row is available with ≥1 shift — and NOTHING
 * else. No row, an unavailable row, or an available row with zero shifts is `[]`: the
 * target is already closed that day. (The fourth closed form, no active location
 * assignment, is decided by the caller before it reads a schedule at all.)
 */
export function effectiveShiftsFor(row: ScheduleRowLike | undefined): ShiftLike[] {
  if (row && row.isAvailable && row.shifts.length > 0) return row.shifts;
  return [];
}

/**
 * The inversion: the shifts that remain OPEN once `blocked` is removed from `shifts`.
 * A block that reaches past the shifts clips to them — it never extends them. Output is
 * sorted and never contains an empty range.
 */
export function subtractWindow(shifts: ShiftLike[], blocked: MinuteRange): ShiftLike[] {
  const remaining: MinuteRange[] = [];
  for (const range of toMinuteRanges(shifts)) {
    if (blocked.end <= range.start || blocked.start >= range.end) {
      remaining.push(range);
      continue;
    }
    if (blocked.start > range.start) remaining.push({ start: range.start, end: blocked.start });
    if (blocked.end < range.end) remaining.push({ start: blocked.end, end: range.end });
  }
  return toShifts(remaining);
}

/**
 * The exception to write for one target. `blocked === null` is the all-day form;
 * otherwise the window form, collapsing to all-day-closed when nothing remains open.
 */
export function blockWriteFor(
  effectiveShifts: ShiftLike[],
  blocked: MinuteRange | null,
): BlockWrite {
  if (blocked === null) return { isAvailable: false, shifts: [] };
  const shifts = subtractWindow(effectiveShifts, blocked);
  if (shifts.length === 0) return { isAvailable: false, shifts: [] };
  return { isAvailable: true, shifts };
}

/**
 * The reverse reading, for the board (U2): given an exception's shape and the visible
 * window, the ranges that are BLOCKED. A closed exception blocks the whole window; an
 * available one blocks the gaps its shifts leave inside the window. An available
 * exception with NO shifts is the engine's `USE_LOCATION_WINDOW` (open on the location's
 * hours) — it blocks nothing.
 */
export function blockedRangesOf(
  exception: BlockWrite,
  window: MinuteRange,
): MinuteRange[] {
  if (!exception.isAvailable) return [window];
  if (exception.shifts.length === 0) return [];
  const open = toMinuteRanges(exception.shifts);
  const gaps: MinuteRange[] = [];
  let cursor = window.start;
  for (const range of open) {
    if (range.start > cursor) gaps.push({ start: cursor, end: Math.min(range.start, window.end) });
    cursor = Math.max(cursor, range.end);
    if (cursor >= window.end) break;
  }
  if (cursor < window.end) gaps.push({ start: cursor, end: window.end });
  return gaps.filter((g) => g.end > g.start);
}

/** The refusal a target with a block already on the date gets (unchanged wording, DNKADM-0373). */
export const ALREADY_BLOCKED_MESSAGE = 'already has a block on this day — remove it below to replace it';

/** What the dialog already knows about a target's exception on the date. */
export interface ExistingExceptionLike {
  exceptionId: string | null;
  isAvailable: boolean;
  shifts: ShiftLike[];
}

/** The decision for one target, made BEFORE any write and shown as the preview (DNKADM-0388 U3). */
export type BlockPlan =
  | { kind: 'closed-already' }
  | { kind: 'refused'; message: string }
  | { kind: 'create'; write: BlockWrite }
  | { kind: 'update'; exceptionId: string; write: BlockWrite };

/**
 * Plan one target's block (DNKADM-0388 U3). A second window on a date that already has an
 * OPEN exception is subtracted from that exception's current shifts (not the weekly row) and
 * updates it in place; a CLOSED existing exception, an all-day request on an existing one, or
 * an existing row with no id keeps the existing refusal. With no existing exception the plan is
 * today's: nothing when the weekly row is closed, else the window form (collapsing to closed
 * when nothing remains). An open exception with NO shifts is the engine's "use the location
 * window" — its hours are not known here, so it is refused rather than closed by mistake.
 */
export function planBlock(
  base: ShiftLike[],
  existing: ExistingExceptionLike | undefined,
  blocked: MinuteRange | null,
): BlockPlan {
  if (existing) {
    if (!existing.isAvailable || blocked === null || existing.exceptionId === null) {
      return { kind: 'refused', message: ALREADY_BLOCKED_MESSAGE };
    }
    if (existing.shifts.length === 0) {
      return {
        kind: 'refused',
        message: 'is open on the location’s hours that day — remove its block below and block again',
      };
    }
    return { kind: 'update', exceptionId: existing.exceptionId, write: blockWriteFor(existing.shifts, blocked) };
  }
  if (base.length === 0) return { kind: 'closed-already' };
  return { kind: 'create', write: blockWriteFor(base, blocked) };
}

/** `"09:00"` → `9:00 AM`; the wire's end-of-day `"00:00"` reads as midnight. */
export function clockLabel(hhmm: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
}

/**
 * The resulting hours for one target, in the staff member's words (DNKADM-0388 U3):
 * `Bay 1 will be open 12:00 PM to 2:30 PM and 4:00 PM to close`, or
 * `Bay 1 will be closed all day`. "close" stands in for the end of the target's own last
 * shift on that day (`closeAt`, the weekly row's last end), so an untouched closing time
 * reads as the closing time rather than a number.
 */
export function openHoursSentence(name: string, write: BlockWrite, closeAt: string | null): string {
  if (!write.isAvailable) return `${name} will be closed all day`;
  const ranges = toMinuteRanges(write.shifts).map((r) => {
    const end = minutesToClock(r.end);
    const endLabel = closeAt !== null && end === closeAt ? 'close' : clockLabel(end);
    return `${clockLabel(minutesToClock(r.start))} to ${endLabel}`;
  });
  return `${name} will be open ${ranges.join(' and ')}`;
}

/** The closing time of a day's base shifts (`"HH:mm"`, `"00:00"` = midnight), or null when closed. */
export function closeOf(base: ShiftLike[]): string | null {
  const ranges = toMinuteRanges(base);
  const last = ranges[ranges.length - 1];
  return last ? minutesToClock(last.end) : null;
}

/**
 * A `"HH:mm"` start/end pair from the dialog's time inputs to a minute range, or a human
 * message when it is not a usable range. Reuses the shared clock rules (an end of
 * `"00:00"` is midnight).
 */
export function parseBlockRange(startTime: string, endTime: string): MinuteRange | string {
  const start = shiftStartMinutes(startTime);
  const end = shiftEndMinutes(endTime);
  if (start === null || end === null) return 'Enter a start and end time for the block.';
  if (start >= end) return 'The block’s start must be before its end.';
  return { start, end };
}

/** Bookings on the selected targets that fall inside the block (all day → the whole date). */
export function reservationsInWindow(
  reservations: Reservation[],
  kind: BlockTargetKind,
  targetIds: ReadonlySet<string>,
  blocked: MinuteRange | null,
  timeZone: string | null,
): Reservation[] {
  return reservations.filter((r) => {
    const id = kind === 'bay' ? r.assignedResourceId : r.assignedStaffId;
    if (!id || !targetIds.has(id)) return false;
    if (blocked === null) return true;
    const startHour = localHourOf(r.startTime, timeZone);
    const endHour = localHourOf(r.endTime, timeZone);
    // An unreadable instant is listed rather than hidden — the operator can judge it.
    if (startHour === null || endHour === null) return true;
    return startHour * 60 < blocked.end && endHour * 60 > blocked.start;
  });
}
